import * as Sentry from '@sentry/react';

import {
    CELL_STATE_LABELS,
    type CellState,
    type CoverageRow,
    DAY_PARTS,
    type DayPartKey,
    type WorkWeekStartDay,
} from '@apps/web/features/coverage/model/coverage_types';
import { type WorkforceAvailabilityCell } from '@libraries/typescript/api_client';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAY_FULL_LABELS = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
];
export const WORK_WEEK_START_INDEX: Record<WorkWeekStartDay, number> = {
    monday: 0,
    tuesday: 1,
    wednesday: 2,
    thursday: 3,
    friday: 4,
    saturday: 5,
    sunday: 6,
};

type CellIndex = {
    readonly cellCount: number;
    readonly positions: ReadonlyMap<string, number>;
};

const CELL_INDEX_BY_ROW = new WeakMap<CoverageRow, CellIndex>();

// Report a broken 28-cell API contract at most once per page load; the
// fallback fires per cell lookup and would otherwise flood Sentry.
let missingCellReported = false;

function cellKey(dayIndex: number, dayPart: DayPartKey): string {
    return `${dayIndex}:${dayPart}`;
}

function buildCellIndex(row: CoverageRow): CellIndex {
    const index = {
        cellCount: row.cells.length,
        positions: new Map(
            row.cells.map((cell, position) => [
                cellKey(cell.day_index, cell.day_part),
                position,
            ]),
        ),
    };
    CELL_INDEX_BY_ROW.set(row, index);
    return index;
}

/**
 * Grid columns follow the org work week, so day_index 0 is the org's
 * start day, not necessarily Monday. These map a day_index to labels.
 */
export function dayLabel(
    dayIndex: number,
    workWeekStartDay: WorkWeekStartDay,
    variant: 'short' | 'full' = 'short',
): string {
    const startIndex = WORK_WEEK_START_INDEX[workWeekStartDay];
    const weekday = (startIndex + dayIndex) % 7;
    return (
        (variant === 'short'
            ? WEEKDAY_LABELS[weekday]
            : WEEKDAY_FULL_LABELS[weekday]) ?? ''
    );
}

export function isWeekendDay(
    dayIndex: number,
    workWeekStartDay: WorkWeekStartDay,
): boolean {
    const startIndex = WORK_WEEK_START_INDEX[workWeekStartDay];
    const weekday = (startIndex + dayIndex) % 7;
    return weekday === 5 || weekday === 6;
}

export function cellAt(
    row: CoverageRow,
    dayIndex: number,
    dayPart: DayPartKey,
): WorkforceAvailabilityCell {
    const key = cellKey(dayIndex, dayPart);
    let index = CELL_INDEX_BY_ROW.get(row);
    if (!index || index.cellCount !== row.cells.length) {
        index = buildCellIndex(row);
    }
    let position = index.positions.get(key);
    let match = position === undefined ? undefined : row.cells[position];
    if (match?.day_index !== dayIndex || match.day_part !== dayPart) {
        index = buildCellIndex(row);
        position = index.positions.get(key);
        match = position === undefined ? undefined : row.cells[position];
    }
    if (match === undefined) {
        // The API contract emits all 28 cells; if one is ever missing,
        // degrade that cell to unknown rather than crashing the page —
        // but report once so the contract break is observable instead
        // of rendering as silently-empty availability.
        if (!missingCellReported) {
            missingCellReported = true;
            Sentry.captureMessage(
                `Missing availability cell d${dayIndex} ${dayPart} for caregiver ${row.caregiver_id}`,
            );
        }
        return {
            day_index: dayIndex,
            day_part: dayPart,
            state: 'unknown',
            available_minutes: 0,
            scheduled_minutes: 0,
            window_minutes: 0,
            has_one_time_override: false,
        };
    }
    return match;
}

export function isOpenState(state: CellState): boolean {
    return state === 'available' || state === 'partially_available';
}

export type SlotAvailability = 'available' | 'unavailable' | 'unknown';

export type SlotAvailabilitySummary = {
    readonly availability: SlotAvailability;
    readonly hasUnknownSlots: boolean;
};

export function summarizeSlotAvailability(
    row: CoverageRow,
    slots: ReadonlyArray<readonly [number, DayPartKey]>,
): SlotAvailabilitySummary | null {
    if (slots.length === 0) {
        return null;
    }
    let hasOpenSlots = false;
    let hasUnknownSlots = false;
    for (const [dayIndex, dayPart] of slots) {
        const { state } = cellAt(row, dayIndex, dayPart);
        hasOpenSlots ||= isOpenState(state);
        hasUnknownSlots ||= state === 'unknown';
    }
    return {
        availability: hasOpenSlots
            ? 'available'
            : hasUnknownSlots
              ? 'unknown'
              : 'unavailable',
        hasUnknownSlots,
    };
}

/**
 * One-sentence summary for the strip's accessible name, so keyboard and
 * screen-reader users get the week at a glance without 28 tab stops.
 */
export function stripSummary(row: CoverageRow): string {
    const counts: Record<CellState, number> = {
        available: 0,
        partially_available: 0,
        scheduled: 0,
        unavailable: 0,
        unknown: 0,
    };
    for (const cell of row.cells) {
        counts[cell.state] += 1;
    }
    const total = row.cells.length;
    if (!row.has_availability_data && counts.scheduled === 0) {
        return `${row.name}: no availability data on file. Open week detail.`;
    }
    if (counts.unknown === total) {
        return `${row.name}: no availability information for this week. Open week detail.`;
    }
    const parts = [
        `available ${counts.available + counts.partially_available} of ${total} windows`,
        `scheduled ${counts.scheduled}`,
        `unavailable ${counts.unavailable}`,
        `no data ${counts.unknown}`,
    ];
    return `${row.name}: ${parts.join(', ')}. Open week detail.`;
}

export function cellTitle(
    row: CoverageRow,
    dayIndex: number,
    dayPart: DayPartKey,
    workWeekStartDay: WorkWeekStartDay,
): string {
    const cell = cellAt(row, dayIndex, dayPart);
    const part = DAY_PARTS.find((entry) => entry.key === dayPart);
    const day = dayLabel(dayIndex, workWeekStartDay);
    return `${day} ${part?.label ?? dayPart} (${part?.timeLabel ?? ''}): ${CELL_STATE_LABELS[cell.state]}`;
}
